import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { get, isNil } from 'lodash';
import { Stack } from 'office-ui-fabric-react/lib/Stack';
import { Label } from 'office-ui-fabric-react/lib/Label';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { Dropdown as FluentDropdown } from 'office-ui-fabric-react/lib/Dropdown';
import clsx from 'clsx';
import styles from './Dropdown.style';
import Callout from '../Callout';

const Dropdown = ({
  field: { name, value },
  form: {
    errors = {},
    isSubmitting = false,
    setFieldValue = () => {},
    setFieldTouched = () => {},
    values = {},
  },
  label,
  placeholder,
  options,
  disabled,
  required,
  callout,
  multiSelect,
  preventDefaultOnEnter,
  className: classNameProp,
  dropdownLabelProps,
  handleCallback,
  initialValue,
  ...other
}) => {
  const className = clsx(classNameProp);
  const multiSelectProp = multiSelect ? { multiSelect: true } : {};

  const selectedKeyProp = {
    // eslint-disable-next-line no-unneeded-ternary
    [multiSelect ? 'selectedKeys' : 'selectedKey']: value,
  };

  const handleKeyDown = e => {
    if (preventDefaultOnEnter && e.keyCode === 13) {
      e.preventDefault();
      return false;
    }
    return true;
  };

  const handleChange = (e, item) => {
    if (item && multiSelect) {
      setFieldValue(
        name,
        item.selected ? [...value, item.key] : value.filter(key => key !== item.key),
      );
      setFieldTouched(name);
    } else if (item) {
      setFieldValue(name, item.key);
      setFieldTouched(name);
    }

    if (handleCallback) {
      handleCallback(values, errors, setFieldValue, setFieldTouched, item.key, name);
    }
  };

  useEffect(() => {
    if (!isNil(initialValue)) {
      setFieldValue(name, initialValue);
      setFieldTouched(name);
    }
  }, [initialValue]);

  const DropdownLabel = () => {
    return (
      <Stack horizontal verticalAlign="start" {...dropdownLabelProps}>
        <Label>{label}</Label>
        {required && <Text styles={styles.asterisk}>*</Text>}
        {callout && <Callout content={callout} />}
      </Stack>
    );
  };

  return (
    <FluentDropdown
      label={label}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onRenderLabel={DropdownLabel}
      disabled={disabled || isSubmitting}
      errorMessage={get(errors, name)}
      className={className}
      placeholder={placeholder}
      options={options}
      styles={styles}
      ariaLabel={required ? `${label} is mandatory` : `${label} dropdown`}
      aria-required={required}
      responsiveMode={{ ResponsiveMode: false }}
      calloutProps={styles.dropdownHeight}
      {...multiSelectProp}
      {...selectedKeyProp}
      {...other}
    />
  );
};

Dropdown.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    onChange: PropTypes.func,
  }).isRequired,
  form: PropTypes.shape({
    touched: PropTypes.shape({}),
    errors: PropTypes.shape({}),
    values: PropTypes.shape({}),
    isSubmitting: PropTypes.bool,
    setFieldValue: PropTypes.func,
    setFieldTouched: PropTypes.func,
  }),
  label: PropTypes.string,
  placeholder: PropTypes.string,
  callout: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      text: PropTypes.string.isRequired,
    }),
  ).isRequired,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  multiSelect: PropTypes.bool,
  preventDefaultOnEnter: PropTypes.bool,
  className: PropTypes.string,
  dropdownLabelProps: PropTypes.object, // React HTML properties
  handleCallback: () => {},
  initialValue: PropTypes.string,
};

Dropdown.defaultProps = {
  label: '',
  className: '',
  dropdownLabelProps: {},
  callout: null,
  disabled: false,
  required: false,
  multiSelect: false,
  placeholder: 'Select',
  preventDefaultOnEnter: false,
  form: {
    touched: {},
    errors: {},
    values: {},
    isSubmitting: false,
    setFieldValue: () => {},
  },
  handleCallback: null,
  initialValue: null,
};

export default Dropdown;
